import * as THREE from 'three';
import Stats from 'three/addons/libs/stats.module.js';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import {
    GodRaysFakeSunShader,
    GodRaysDepthMaskShader,
    GodRaysCombineShader,
    GodRaysGenerateShader
} from 'three/addons/shaders/GodRaysShader.js';

let container: HTMLElement;
let stats: Stats;
let camera: THREE.PerspectiveCamera;
let scene: THREE.Scene;
let renderer: THREE.WebGLRenderer;
let materialDepth: THREE.MeshDepthMaterial;
let sphereMesh: THREE.Mesh;
let treeObject: THREE.Object3D | null = null;
let controls: OrbitControls;

const sunPosition = new THREE.Vector3(0, 1000, -1000);
const clipPosition = new THREE.Vector4();
const screenSpacePosition = new THREE.Vector3();

const postprocessing: any = { enabled: true };
const orbitRadius = 200;

// ------------------------------------------------------------------
// Background & Sun Colors
// ------------------------------------------------------------------
let bgColor = 0x000511;
let sunColor = 0xffee00;

// ------------------------------------------------------------------
// God Ray Intensity + Transition Variables
// ------------------------------------------------------------------
let godrayIntensity = 0;                          // Start at 0
let currentGodrayIntensity = 0;                   // Track current intensity
const targetGodrayIntensity = 0.75;               // Final intensity
let godrayIntensityTransitionStartTime: number | null = null;
const godrayIntensityTransitionDuration = 3000;    // 1 second (1000 ms)

// ------------------------------------------------------------------
// Smooth Sun Color Transition
// ------------------------------------------------------------------
let currentSunColor = 0xffee00;
let colorTransitionStartTime: number | null = null;
let targetSunColor: number | null = null;

const godrayRenderTargetResolutionMultiplier = 1.0 / 4.0;

// ------------------------------------------------------------------
// Public Functions to Adjust Colors / Intensity
// ------------------------------------------------------------------
export function setBackgroundColor(color: number) {
    bgColor = color;
    if (postprocessing.godraysFakeSunUniforms) {
        postprocessing.godraysFakeSunUniforms.bgColor.value.setHex(bgColor);
    }
}

export function setGodrayIntensity(intensity: number) {
    godrayIntensity = intensity;
    currentGodrayIntensity = intensity; // keep local tracking in sync
    if (postprocessing.godrayCombineUniforms) {
        postprocessing.godrayCombineUniforms.fGodRayIntensity.value = godrayIntensity;
    }
}

/**
 * Initiates a smooth color transition for the sunColor over 1 second
 */
export function setSunColor(color: number) {
    if (color === currentSunColor) return; // no change needed

    targetSunColor = color;
    currentSunColor = sunColor; // store the current sun color for interpolation
    colorTransitionStartTime = performance.now();

    requestAnimationFrame(updateSunColorTransition);
}

// ------------------------------------------------------------------
// Internal Functions
// ------------------------------------------------------------------
function updateSunColorTransition(timestamp: number) {
    if (!colorTransitionStartTime || !targetSunColor) return;

    const elapsed = timestamp - colorTransitionStartTime;
    const duration = 1000; // 1 second for sun color transition

    if (elapsed >= duration) {
        // Transition complete
        sunColor = targetSunColor;
        if (postprocessing.godraysFakeSunUniforms) {
            postprocessing.godraysFakeSunUniforms.sunColor.value.setHex(sunColor);
        }
        colorTransitionStartTime = null;
        targetSunColor = null;
        return;
    }

    const progress = elapsed / duration;
    const startColor = new THREE.Color(currentSunColor);
    const endColor = new THREE.Color(targetSunColor);

    // Lerp each component
    const interpolatedColor = new THREE.Color(
        startColor.r + (endColor.r - startColor.r) * progress,
        startColor.g + (endColor.g - startColor.g) * progress,
        startColor.b + (endColor.b - startColor.b) * progress
    );

    sunColor = interpolatedColor.getHex();
    if (postprocessing.godraysFakeSunUniforms) {
        postprocessing.godraysFakeSunUniforms.sunColor.value.copy(interpolatedColor);
    }

    requestAnimationFrame(updateSunColorTransition);
}

/**
 * Smoothly update god ray intensity from 0 -> 0.75
 */
function updateGodrayIntensityTransition(timestamp: number) {
    if (godrayIntensityTransitionStartTime === null) return;

    const elapsed = timestamp - godrayIntensityTransitionStartTime;
    if (elapsed >= godrayIntensityTransitionDuration) {
        // Transition done: fix intensity at final value
        setGodrayIntensity(targetGodrayIntensity);
        godrayIntensityTransitionStartTime = null;
        return;
    }

    const progress = elapsed / godrayIntensityTransitionDuration;
    const interpolatedValue = 0 + (targetGodrayIntensity - 0) * progress;
    setGodrayIntensity(interpolatedValue);

    requestAnimationFrame(updateGodrayIntensityTransition);
}

/**
 * God Rays Filtering Helpers
 */
function getStepSize(filterLen: number, tapsPerPass: number, pass: number) {
    return filterLen * Math.pow(tapsPerPass, -pass);
}

function filterGodRays(inputTex: THREE.Texture, renderTarget: THREE.WebGLRenderTarget, stepSize: number) {
    postprocessing.scene.overrideMaterial = postprocessing.materialGodraysGenerate;
    postprocessing.godrayGenUniforms['fStepSize'].value = stepSize;
    postprocessing.godrayGenUniforms['tInput'].value = inputTex;

    renderer.setRenderTarget(renderTarget);
    renderer.render(postprocessing.scene, postprocessing.camera);
    postprocessing.scene.overrideMaterial = null;
}

/**
 * Setup Postprocessing Pipeline
 */
function initPostprocessing(renderTargetWidth: number, renderTargetHeight: number) {
    postprocessing.scene = new THREE.Scene();
    postprocessing.camera = new THREE.OrthographicCamera(
        -0.5, 0.5,
         0.5, -0.5,
        -10000, 10000
    );
    postprocessing.camera.position.z = 100;
    postprocessing.scene.add(postprocessing.camera);

    // Primary Targets
    postprocessing.rtTextureColors = new THREE.WebGLRenderTarget(
        renderTargetWidth,
        renderTargetHeight,
        { type: THREE.HalfFloatType }
    );
    postprocessing.rtTextureDepth = new THREE.WebGLRenderTarget(
        renderTargetWidth,
        renderTargetHeight,
        { type: THREE.HalfFloatType }
    );
    postprocessing.rtTextureDepthMask = new THREE.WebGLRenderTarget(
        renderTargetWidth,
        renderTargetHeight,
        { type: THREE.HalfFloatType }
    );

    // Lower Resolution Targets for God Rays
    const adjustedWidth = renderTargetWidth * godrayRenderTargetResolutionMultiplier;
    const adjustedHeight = renderTargetHeight * godrayRenderTargetResolutionMultiplier;
    postprocessing.rtTextureGodRays1 = new THREE.WebGLRenderTarget(adjustedWidth, adjustedHeight, {
        type: THREE.HalfFloatType
    });
    postprocessing.rtTextureGodRays2 = new THREE.WebGLRenderTarget(adjustedWidth, adjustedHeight, {
        type: THREE.HalfFloatType
    });

    // Depth Mask
    const godraysMaskShader = GodRaysDepthMaskShader;
    postprocessing.godrayMaskUniforms = THREE.UniformsUtils.clone(godraysMaskShader.uniforms);
    postprocessing.materialGodraysDepthMask = new THREE.ShaderMaterial({
        uniforms: postprocessing.godrayMaskUniforms,
        vertexShader: godraysMaskShader.vertexShader,
        fragmentShader: godraysMaskShader.fragmentShader
    });

    // God Rays Generate
    const godraysGenShader = GodRaysGenerateShader;
    postprocessing.godrayGenUniforms = THREE.UniformsUtils.clone(godraysGenShader.uniforms);
    postprocessing.materialGodraysGenerate = new THREE.ShaderMaterial({
        uniforms: postprocessing.godrayGenUniforms,
        vertexShader: godraysGenShader.vertexShader,
        fragmentShader: godraysGenShader.fragmentShader
    });

    // Combine
    const godraysCombineShader = GodRaysCombineShader;
    postprocessing.godrayCombineUniforms = THREE.UniformsUtils.clone(godraysCombineShader.uniforms);
    postprocessing.materialGodraysCombine = new THREE.ShaderMaterial({
        uniforms: postprocessing.godrayCombineUniforms,
        vertexShader: godraysCombineShader.vertexShader,
        fragmentShader: godraysCombineShader.fragmentShader
    });

    // Fake Sun
    const godraysFakeSunShader = GodRaysFakeSunShader;
    postprocessing.godraysFakeSunUniforms = THREE.UniformsUtils.clone(godraysFakeSunShader.uniforms);
    postprocessing.materialGodraysFakeSun = new THREE.ShaderMaterial({
        uniforms: postprocessing.godraysFakeSunUniforms,
        vertexShader: godraysFakeSunShader.vertexShader,
        fragmentShader: godraysFakeSunShader.fragmentShader
    });

    // Initialize background and sun color
    postprocessing.godraysFakeSunUniforms.bgColor.value.setHex(bgColor);
    postprocessing.godraysFakeSunUniforms.sunColor.value.setHex(sunColor);

    // God ray intensity (we'll be animating this)
    postprocessing.godrayCombineUniforms.fGodRayIntensity.value = godrayIntensity;

    // Fullscreen quad for rendering
    postprocessing.quad = new THREE.Mesh(
        new THREE.PlaneGeometry(1.0, 1.0),
        postprocessing.materialGodraysGenerate
    );
    postprocessing.quad.position.z = -9900;
    postprocessing.scene.add(postprocessing.quad);
}

/**
 * Handle Window Resize
 */
function onWindowResize() {
    const renderTargetWidth = container.clientWidth;
    const renderTargetHeight = container.clientHeight;

    camera.aspect = renderTargetWidth / renderTargetHeight;
    camera.updateProjectionMatrix();

    renderer.setSize(renderTargetWidth, renderTargetHeight);
    postprocessing.rtTextureColors.setSize(renderTargetWidth, renderTargetHeight);
    postprocessing.rtTextureDepth.setSize(renderTargetWidth, renderTargetHeight);
    postprocessing.rtTextureDepthMask.setSize(renderTargetWidth, renderTargetHeight);

    const adjustedWidth = renderTargetWidth * godrayRenderTargetResolutionMultiplier;
    const adjustedHeight = renderTargetHeight * godrayRenderTargetResolutionMultiplier;
    postprocessing.rtTextureGodRays1.setSize(adjustedWidth, adjustedHeight);
    postprocessing.rtTextureGodRays2.setSize(adjustedWidth, adjustedHeight);
}

/**
 * Render loop
 */
function render() {
    const time = Date.now() / 4000;

    // Orbit sphere around the scene
    sphereMesh.position.x = orbitRadius * Math.cos(time);
    sphereMesh.position.z = orbitRadius * Math.sin(time) - 100;

    // Slowly rotate the tree
    if (treeObject) {
        treeObject.rotation.y = time * 0.25; 
    }

    if (postprocessing.enabled) {
        // Project sun position into screen space
        clipPosition.set(sunPosition.x, sunPosition.y, sunPosition.z, 1.0);
        clipPosition.applyMatrix4(camera.matrixWorldInverse).applyMatrix4(camera.projectionMatrix);
        
        clipPosition.x /= clipPosition.w;
        clipPosition.y /= clipPosition.w;

        screenSpacePosition.set(
            (clipPosition.x + 1) / 2,
            (clipPosition.y + 1) / 2,
            clipPosition.z
        );

        // Update uniforms with sun position
        postprocessing.godrayGenUniforms['vSunPositionScreenSpace'].value.copy(screenSpacePosition);
        postprocessing.godraysFakeSunUniforms['vSunPositionScreenSpace'].value.copy(screenSpacePosition);

        // 1) Render sun & scene colors to rtTextureColors
        renderer.setRenderTarget(postprocessing.rtTextureColors);
        renderer.clear(true, true, false);

        const sunsqH = 0.74 * container.clientHeight;
        const sunsqW = 0.74 * container.clientHeight;

        screenSpacePosition.x *= container.clientWidth;
        screenSpacePosition.y *= container.clientHeight;

        renderer.setScissor(
            screenSpacePosition.x - sunsqW / 2,
            screenSpacePosition.y - sunsqH / 2,
            sunsqW,
            sunsqH
        );
        renderer.setScissorTest(true);

        postprocessing.godraysFakeSunUniforms['fAspect'].value = container.clientWidth / container.clientHeight;

        postprocessing.scene.overrideMaterial = postprocessing.materialGodraysFakeSun;
        renderer.setRenderTarget(postprocessing.rtTextureColors);
        renderer.render(postprocessing.scene, postprocessing.camera);
        renderer.setScissorTest(false);

        // Render the actual 3D scene
        scene.overrideMaterial = null;
        renderer.setRenderTarget(postprocessing.rtTextureColors);
        renderer.render(scene, camera);

        // 2) Render depth into rtTextureDepth
        scene.overrideMaterial = materialDepth;
        renderer.setRenderTarget(postprocessing.rtTextureDepth);
        renderer.clear();
        renderer.render(scene, camera);

        // 3) Generate godrays mask
        postprocessing.godrayMaskUniforms['tInput'].value = postprocessing.rtTextureDepth.texture;
        postprocessing.scene.overrideMaterial = postprocessing.materialGodraysDepthMask;
        renderer.setRenderTarget(postprocessing.rtTextureDepthMask);
        renderer.render(postprocessing.scene, postprocessing.camera);

        // 4) Multiple passes to blur & intensify rays
        const filterLen = 1.0;
        const TAPS_PER_PASS = 6.0;

        filterGodRays(
            postprocessing.rtTextureDepthMask.texture,
            postprocessing.rtTextureGodRays2,
            getStepSize(filterLen, TAPS_PER_PASS, 1.0)
        );
        filterGodRays(
            postprocessing.rtTextureGodRays2.texture,
            postprocessing.rtTextureGodRays1,
            getStepSize(filterLen, TAPS_PER_PASS, 2.0)
        );
        filterGodRays(
            postprocessing.rtTextureGodRays1.texture,
            postprocessing.rtTextureGodRays2,
            getStepSize(filterLen, TAPS_PER_PASS, 3.0)
        );

        // 5) Combine godrays & scene
        postprocessing.godrayCombineUniforms['tColors'].value = postprocessing.rtTextureColors.texture;
        postprocessing.godrayCombineUniforms['tGodRays'].value = postprocessing.rtTextureGodRays2.texture;

        postprocessing.scene.overrideMaterial = postprocessing.materialGodraysCombine;
        renderer.setRenderTarget(null);
        renderer.render(postprocessing.scene, postprocessing.camera);
        postprocessing.scene.overrideMaterial = null;

    } else {
        // If not using postprocessing, render normally
        renderer.setRenderTarget(null);
        renderer.clear();
        renderer.render(scene, camera);
    }
}

/**
 * Animation loop
 */
function animate() {
    requestAnimationFrame(animate);
    stats.begin();
    controls.update();
    render();
    stats.end();
}

/**
 * Initialize Scene
 */
export function init(containerElement: HTMLElement) {
    container = containerElement;

    camera = new THREE.PerspectiveCamera(70, container.clientWidth / container.clientHeight, 1, 3000);
    camera.position.set(14.530, -112, 225);

    scene = new THREE.Scene();

    // Depth material for god rays
    materialDepth = new THREE.MeshDepthMaterial();

    // Load tree model
    const loader = new OBJLoader();
    loader.load('models/tree.obj', (object) => {
        object.position.set(0, -150, -150);
        object.scale.multiplyScalar(400);
        treeObject = object;
        // Rotate so it's facing a nicer orientation
        treeObject.rotation.y = Math.PI / 2;
        scene.add(object);
    });

    // Black sphere that orbits (the "occluder")
    const geo = new THREE.SphereGeometry(1, 20, 10);
    sphereMesh = new THREE.Mesh(
        geo,
        new THREE.MeshBasicMaterial({ color: 0x000000 })
    );
    sphereMesh.scale.multiplyScalar(20);
    scene.add(sphereMesh);

    // Init renderer
    renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setClearColor(0xffffff);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.autoClear = false;
    container.appendChild(renderer.domElement);

    // Orbit controls
    controls = new OrbitControls(camera, renderer.domElement);
    controls.minDistance = 50;
    controls.maxDistance = 500;
    controls.autoRotateSpeed = 1.0;
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minAzimuthAngle = -Math.PI / 3;
    controls.maxAzimuthAngle =  Math.PI / 3;
    controls.target.set(38.0932819, 49.340, -134);

    // Stats
    stats = new Stats();
    // container.appendChild(stats.dom);

    // Resize
    window.addEventListener('resize', onWindowResize);

    // Setup postprocessing pipeline
    initPostprocessing(container.clientWidth, container.clientHeight);

    // ----------------------------------------------------------------------
    // Kick off the god ray intensity transition from 0 -> 0.75 over 1 second
    // ----------------------------------------------------------------------
    setGodrayIntensity(0);  // Ensure we start at 0
    godrayIntensityTransitionStartTime = performance.now() + 1000; // wait one second in black
    requestAnimationFrame(updateGodrayIntensityTransition);

    // Start rendering
    animate();
}

/**
 * Cleanup
 */
// No dispose function needed - React will handle cleanup
